/**
 * UI state of the shell (Zustand): sidebar preference (persisted), the Lainnya sheet and
 * the search palette, and the device facts the shell displays: browser connectivity, the
 * worker's SyncState, the database ready info and the tab lock. Data never lives here; it
 * lives in SQLite behind the worker.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { DbLockState } from '../../db/lock'
import { type DbReadyInfo, INITIAL_SYNC_STATE, type SyncState } from '../../db/protocol'

export interface UpdateState {
  /** A new service worker is waiting. */
  needRefresh: boolean
  /** The person chose "Nanti" for this version. */
  dismissed: boolean
}

export interface UiState {
  /** null follows the breakpoint: expanded from 1024 px, icon rail below. */
  sidebarOpen: boolean | null
  setSidebarOpen(open: boolean): void
  lainnyaOpen: boolean
  setLainnyaOpen(open: boolean): void
  cariOpen: boolean
  setCariOpen(open: boolean): void
  online: boolean
  setOnline(online: boolean): void
  sync: SyncState
  setSync(sync: SyncState): void
  ready: DbReadyInfo | null
  setReady(info: DbReadyInfo | null): void
  lock: DbLockState
  setLock(lock: DbLockState): void
  /** navigator.storage.persist() result; null until answered. */
  persisted: boolean | null
  setPersisted(persisted: boolean): void
  /** Forms with unsaved changes; the update prompt never auto-applies while any is open. */
  dirtyForms: number
  markDirty(delta: 1 | -1): void
  update: UpdateState
  setUpdate(patch: Partial<UpdateState>): void
}

export const useUi = create<UiState>()(
  persist(
    (set) => ({
      sidebarOpen: null,
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      lainnyaOpen: false,
      setLainnyaOpen: (lainnyaOpen) => set({ lainnyaOpen }),
      cariOpen: false,
      setCariOpen: (cariOpen) => set({ cariOpen }),
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      setOnline: (online) => set({ online }),
      sync: INITIAL_SYNC_STATE,
      setSync: (sync) => set({ sync }),
      ready: null,
      setReady: (ready) => set({ ready }),
      lock: { status: 'memulai' },
      setLock: (lock) => set({ lock }),
      persisted: null,
      setPersisted: (persisted) => set({ persisted }),
      dirtyForms: 0,
      markDirty: (delta) => set((state) => ({ dirtyForms: Math.max(0, state.dirtyForms + delta) })),
      update: { needRefresh: false, dismissed: false },
      setUpdate: (patch) => set((state) => ({ update: { ...state.update, ...patch } })),
    }),
    {
      // Legacy storage name from SIMU; kept so devices keep their data (pinned: test/nama-warisan.test.ts).
      name: 'simu:ui',
      partialize: (state) => ({ sidebarOpen: state.sidebarOpen }),
    },
  ),
)
